import {Avatar, Button, Card, type CardProps} from 'platform/components';
import {
  Heading,
  HStack,
  Show,
  Text,
  type ThemeColorPath,
  type ThemeIconKey,
  VStack,
} from 'platform/foundation';
import {CurrencyFormat} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {type RequiredTestIdProps} from 'shared/web';

import type {CustomerInsightsAlert} from '../../../types/CustomerInsightsViewModel';
import {CustomerInsightsClickableRow} from './CustomerInsightsClickableRow';
import {CustomerInsightsDateMetadata} from './CustomerInsightsDateMetadata';
import {CustomerInsightsLink} from './CustomerInsightsLink';

interface CustomerInsightsAlertCardProps extends RequiredTestIdProps {
  alert: CustomerInsightsAlert;
  title: string;
}

const ALERT_STYLES: Record<
  CustomerInsightsAlert['kind'],
  {
    background: ThemeColorPath;
    color: ThemeColorPath;
    colorStripe: NonNullable<CardProps['colorStripe']>;
    icon: ThemeIconKey;
  }
> = {
  overdueInvoices: {
    background: 'palettes.red.20.100',
    color: 'palettes.red.80.100',
    colorStripe: 'red',
    icon: 'sidebar/accounting_invoices',
  },
  overdueTasks: {
    background: 'palettes.orange.20.100',
    color: 'palettes.orange.80.100',
    colorStripe: 'orange',
    icon: 'action/assignment_turned_in',
  },
};

export function CustomerInsightsAlertCard(props: CustomerInsightsAlertCardProps) {
  const style = ALERT_STYLES[props.alert.kind];
  const card = (
    <CustomerInsightsClickableRow href={props.alert.href}>
      <Card
        variant="inlineWhite"
        colorStripe={style.colorStripe}
        data-testid={props['data-testid']}
      >
        <HStack spacing={3} align="center" minWidth={0}>
          <Avatar
            size="default"
            variant="square"
            icon={style.icon}
            color={style.background}
            iconColor={style.color}
            data-testid={`${props['data-testid']}-icon`}
          />
          <VStack spacing={1} minWidth={0} grow={1}>
            <Heading size={5}>{props.title}</Heading>
            {match(props.alert.href)
              .with(Pattern.string, (href) => (
                <CustomerInsightsLink
                  href={href}
                  title={props.alert.title}
                  size="xSmall"
                  overflowWrap="anywhere"
                />
              ))
              .otherwise(() => (
                <Text size="small" alternative overflowWrap="anywhere">
                  {props.alert.title}
                </Text>
              ))}
          </VStack>
          <VStack spacing={1} align="flex-end" data-testid={`${props['data-testid']}-trailing`}>
            <Show when={props.alert.amount}>
              <Text size="small" alternative noWrap data-testid={`${props['data-testid']}-amount`}>
                <CurrencyFormat
                  number={props.alert.amount?.amount ?? 0}
                  currency={props.alert.amount?.currency ?? ''}
                />
              </Text>
            </Show>
            {match(props.alert.dueAt)
              .with(Pattern.string, (dueAt) => (
                <CustomerInsightsDateMetadata
                  occurredAt={dueAt}
                  data-testid={`${props['data-testid']}-dueDate`}
                />
              ))
              .otherwise(() => null)}
          </VStack>
        </HStack>
      </Card>
    </CustomerInsightsClickableRow>
  );

  const hasAdditionalItems =
    (props.alert.additionalCount ?? 0) > 0 && props.alert.additionalItemsHref !== undefined;

  return match({hasAdditionalItems, additionalItemsHref: props.alert.additionalItemsHref})
    .with(
      {hasAdditionalItems: true, additionalItemsHref: Pattern.string},
      ({additionalItemsHref}) => (
        <VStack spacing={1}>
          {card}
          <Button
            size="small"
            variant="link"
            title={`+${props.alert.additionalCount ?? 0}`}
            to={additionalItemsHref}
            aria-label={`${props.title}: +${props.alert.additionalCount ?? 0}`}
          />
        </VStack>
      )
    )
    .otherwise(() => card);
}
